import React, { useEffect, useRef } from 'react'
import { useFormikContext } from 'formik'
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Checkbox } from '../form'
import { InformationIcon } from '../assetsComponents'
import { getCoachSchedulingFlags } from './schedulingHelpers'
import {
  isBioInMailAvailable,
  getBioInMailDisabledMessage,
  canBulkAssignIncludeBioInMail,
  getBulkBioInMailDisabledMessage,
  getBulkBioInMailPartialWarning,
} from '../scheduling/schedulingPreconditions'

const fieldName = 'includeBioInMail'
const fieldLabel = 'Include Bio in the Schedule Email?'
const manualSchedulingMessage =
  'Including the bio in the schedule email is only available with Platform Scheduling.'

const getBioAvailabilityState = ({
  values,
  coaches,
  coachSchedulingFlagsByCoachId,
  isBulkAssignSOWSection,
}) => {
  if (isBulkAssignSOWSection) {
    const bulkAssignCoachIds = values.bulkAssignCoachIds || []
    return {
      isAvailable: canBulkAssignIncludeBioInMail(
        bulkAssignCoachIds,
        coachSchedulingFlagsByCoachId,
        values.sowType,
      ),
      disabledMessage: getBulkBioInMailDisabledMessage(
        bulkAssignCoachIds,
        coaches,
        coachSchedulingFlagsByCoachId,
        values.sowType,
      ),
      warningMessage: getBulkBioInMailPartialWarning(
        bulkAssignCoachIds,
        coaches,
        coachSchedulingFlagsByCoachId,
        values.sowType,
      ),
    }
  }

  const coachSchedulingFlags = getCoachSchedulingFlags(
    coaches,
    values.sowCoachId,
    coachSchedulingFlagsByCoachId,
  )
  return {
    isAvailable: isBioInMailAvailable(coachSchedulingFlags, values.sowType),
    disabledMessage: getBioInMailDisabledMessage(
      coachSchedulingFlags,
      values.sowType,
    ),
  }
}

const getBioInMailState = (params) => {
  const bioState = getBioAvailabilityState(params)
  const isManualScheduling =
    params.values.isPlatformScheduling === false ||
    params.values.isPlatformScheduling === 'false'

  if (bioState.isAvailable && isManualScheduling) {
    return { isAvailable: false, disabledMessage: manualSchedulingMessage }
  }
  return bioState
}

// Re-checks the field when the coach, SOW type or scheduling type changes; forced off when unavailable
const useBioInMailDefaultValue = ({
  values,
  setFieldValue,
  isAvailable,
  isEnabled,
  isBulkAssignSOWSection,
}) => {
  const coachKey = isBulkAssignSOWSection
    ? (values.bulkAssignCoachIds || []).join(',')
    : values.sowCoachId
  const eligibilityKey = `${coachKey}|${values.sowType}|${isAvailable}`
  const prevEligibilityKey = useRef(null)
  const prevSowCopy = useRef(values.sowCopy)

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    const isCopyOperation =
      Array.isArray(values.sowCopy) &&
      values.sowCopy.length > 0 &&
      prevSowCopy.current?.[0]?.value !== values.sowCopy[0]?.value
    const hasValue =
      values.includeBioInMail === true || values.includeBioInMail === false

    if (!isAvailable) {
      if (values.includeBioInMail !== false) {
        setFieldValue(fieldName, false)
      }
    } else if (
      !hasValue ||
      (prevEligibilityKey.current !== eligibilityKey && !isCopyOperation)
    ) {
      if (values.includeBioInMail !== true) {
        setFieldValue(fieldName, true)
      }
    }

    prevEligibilityKey.current = eligibilityKey
    prevSowCopy.current = values.sowCopy
  }, [
    eligibilityKey,
    isAvailable,
    isEnabled,
    values.includeBioInMail,
    values.sowCopy,
    setFieldValue,
  ])
}

/**
 * "Include Bio in the Schedule Email?" checkbox for SOW form
 * @param {Object} props
 * @param {Array} props.coaches - Coach options with scheduling flags
 * @param {Object} props.coachSchedulingFlagsByCoachId - Stored flags keyed by coach id
 * @param {string} props.planId - Plan id from route ('create' for new SOW)
 * @param {boolean} props.isBulkAssignSOWSection - Whether used in bulk assign SOW
 * @param {boolean} props.disabled - Whether the form field is disabled
 */
const IncludeBioInMailField = ({
  coaches = [],
  coachSchedulingFlagsByCoachId = {},
  planId,
  isBulkAssignSOWSection = false,
  disabled = false,
}) => {
  const { values, setFieldValue } = useFormikContext()
  const { isAvailable, disabledMessage, warningMessage } = getBioInMailState({
    values,
    coaches,
    coachSchedulingFlagsByCoachId,
    isBulkAssignSOWSection,
  })

  useBioInMailDefaultValue({
    values,
    setFieldValue,
    isAvailable,
    isEnabled: planId === 'create' || isBulkAssignSOWSection,
    isBulkAssignSOWSection,
  })

  return (
    <Row className='mb-1 px-2' xs={1} sm={1} md={1} lg={2}>
      <Col className='col-lg-2' />
      <Col className='d-flex col-lg-10 text-left align-items-start pl-2'>
        {isAvailable ? (
          <>
            <Checkbox
              name={fieldName}
              type='checkbox'
              label={fieldLabel}
              formGroupProps={{ className: 'mb-0' }}
              disabled={disabled}
            />
            {warningMessage && (
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip className='font-open-sans font-16 font-semibold leading-7 text-center header-tooltip inviteonly-content-text modal-tooltip'>
                    {warningMessage}
                  </Tooltip>
                }>
                <span className='d-inline-flex align-items-center ml-2'>
                  <InformationIcon iconClasses='align-self-center' />
                </span>
              </OverlayTrigger>
            )}
          </>
        ) : (
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip className='font-open-sans font-16 font-semibold leading-7 text-center header-tooltip inviteonly-content-text modal-tooltip'>
                {disabledMessage}
              </Tooltip>
            }>
            <span className='d-inline-flex align-items-center cursor-disabled'>
              <Checkbox
                name={fieldName}
                type='checkbox'
                label={fieldLabel}
                formGroupProps={{ className: 'mb-0' }}
                labelProps={{ className: 'text-muted' }}
                style={{ pointerEvents: 'none' }}
                disabled
              />
              <span className='d-inline-flex align-items-center ml-2'>
                <InformationIcon iconClasses='align-self-center' />
              </span>
            </span>
          </OverlayTrigger>
        )}
      </Col>
    </Row>
  )
}

export default IncludeBioInMailField
